/**
 * Just enough EXIF to do two jobs, both of which matter to a shop owner photographing a garment
 * on a phone:
 *
 *  1. ORIENTATION. Phone cameras do not rotate the sensor data; they record which way up the
 *     phone was in an EXIF tag and leave the pixels alone. Ignore that tag and every photo taken
 *     in portrait appears on its side. `createImageBitmap(blob, { imageOrientation: 'from-image' })`
 *     handles this in modern browsers, but not all of them, so the tag is read here and applied
 *     manually as a fallback.
 *
 *  2. PROOF THAT IT IS GONE. EXIF also carries a GPS block — a shop's home address, attached to
 *     every photo of a customer's blouse. Re-encoding through a canvas drops all of it, because a
 *     canvas holds pixels and nothing else. These readers are what let a test assert that: parse
 *     the input, confirm the metadata was there, parse the output, confirm it is not.
 *
 * The parser walks JPEG segments rather than pulling in a metadata library — a few hundred bytes
 * of header is all that is needed, and this runs on every photo on a phone.
 */

export interface ExifSummary {
  /** True when the file carries an APP1 Exif segment at all. */
  present: boolean;
  /** EXIF orientation, 1–8. Undefined when absent or unreadable. */
  orientation: number | undefined;
  /** True when a GPS IFD is present — the tag we most want to be rid of. */
  hasGps: boolean;
}

const JPEG_SOI = 0xffd8;
const MARKER_APP1 = 0xffe1;
const MARKER_SOS = 0xffda;
const TAG_ORIENTATION = 0x0112;
const TAG_GPS_IFD = 0x8825;

/** Parse whatever EXIF a JPEG carries. Never throws — a corrupt header is just "no metadata". */
export function readExif(data: ArrayBuffer): ExifSummary {
  const empty: ExifSummary = { present: false, orientation: undefined, hasGps: false };
  try {
    const view = new DataView(data);
    if (view.byteLength < 4 || view.getUint16(0) !== JPEG_SOI) return empty;

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset);
      // Every segment marker starts 0xFF; anything else means we have lost the thread.
      if ((marker & 0xff00) !== 0xff00) return empty;
      // Image data begins here; there is no metadata past it.
      if (marker === MARKER_SOS) return empty;

      const length = view.getUint16(offset + 2);
      if (length < 2) return empty;

      if (marker === MARKER_APP1 && isExifHeader(view, offset + 4)) {
        return readExifSegment(view, offset + 10) ?? { ...empty, present: true };
      }
      offset += 2 + length;
    }
    return empty;
  } catch {
    return empty;
  }
}

/** `Exif\0\0` */
function isExifHeader(view: DataView, at: number): boolean {
  if (at + 6 > view.byteLength) return false;
  return (
    view.getUint8(at) === 0x45 &&
    view.getUint8(at + 1) === 0x78 &&
    view.getUint8(at + 2) === 0x69 &&
    view.getUint8(at + 3) === 0x66 &&
    view.getUint8(at + 4) === 0x00 &&
    view.getUint8(at + 5) === 0x00
  );
}

/** `tiffStart` is the first byte of the TIFF header; every offset inside is relative to it. */
function readExifSegment(view: DataView, tiffStart: number): ExifSummary | undefined {
  if (tiffStart + 8 > view.byteLength) return undefined;

  const byteOrder = view.getUint16(tiffStart);
  // 'II' = Intel/little-endian, 'MM' = Motorola/big-endian.
  const littleEndian = byteOrder === 0x4949;
  if (!littleEndian && byteOrder !== 0x4d4d) return undefined;
  if (view.getUint16(tiffStart + 2, littleEndian) !== 0x002a) return undefined;

  const ifdOffset = view.getUint32(tiffStart + 4, littleEndian);
  const ifdStart = tiffStart + ifdOffset;
  if (ifdStart + 2 > view.byteLength) return undefined;

  const entryCount = view.getUint16(ifdStart, littleEndian);
  let orientation: number | undefined;
  let hasGps = false;

  for (let index = 0; index < entryCount; index += 1) {
    const entry = ifdStart + 2 + index * 12;
    if (entry + 12 > view.byteLength) break;

    const tag = view.getUint16(entry, littleEndian);
    if (tag === TAG_ORIENTATION) {
      // SHORT, count 1 — the value sits in the first two bytes of the value field.
      const value = view.getUint16(entry + 8, littleEndian);
      if (value >= 1 && value <= 8) orientation = value;
    } else if (tag === TAG_GPS_IFD) {
      hasGps = true;
    }
  }

  return { present: true, orientation, hasGps };
}

/** Convenience wrappers, so call sites read as what they are asking. */
export function readExifOrientation(data: ArrayBuffer): number | undefined {
  return readExif(data).orientation;
}

export function hasGpsMetadata(data: ArrayBuffer): boolean {
  return readExif(data).hasGps;
}

export interface OrientationTransform {
  rotateDegrees: 0 | 90 | 180 | 270;
  flipHorizontal: boolean;
  /** True when the rotation exchanges width and height. */
  swapsAxes: boolean;
}

/**
 * The canvas transform that puts an image the right way up. The eight EXIF orientations cover
 * every combination of a quarter-turn and a mirror; 5–8 are the mirrored ones, which phones
 * produce from selfie cameras.
 */
export function orientationTransform(orientation: number | undefined): OrientationTransform {
  switch (orientation) {
    case 2:
      return { rotateDegrees: 0, flipHorizontal: true, swapsAxes: false };
    case 3:
      return { rotateDegrees: 180, flipHorizontal: false, swapsAxes: false };
    case 4:
      return { rotateDegrees: 180, flipHorizontal: true, swapsAxes: false };
    case 5:
      return { rotateDegrees: 90, flipHorizontal: true, swapsAxes: true };
    case 6:
      return { rotateDegrees: 90, flipHorizontal: false, swapsAxes: true };
    case 7:
      return { rotateDegrees: 270, flipHorizontal: true, swapsAxes: true };
    case 8:
      return { rotateDegrees: 270, flipHorizontal: false, swapsAxes: true };
    case 1:
    default:
      return { rotateDegrees: 0, flipHorizontal: false, swapsAxes: false };
  }
}
